/**
 * Defensive helpers for rendering data from the AI estimator.
 * The LLM occasionally returns unexpected shapes (objects instead of strings,
 * missing fields, nested wrappers). These coerce values into render-safe primitives
 * so the React tree never throws "Objects are not valid as a React child" or
 * "Cannot read properties of undefined".
 */

export function safeString(v, fallback = "") {
  if (v === null || v === undefined) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // Objects / arrays — fall back to JSON or fallback. Never throw.
  try {
    return JSON.stringify(v);
  } catch {
    return fallback;
  }
}

export function safeNumber(v, fallback = 0) {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

export function safeArray(v) {
  if (Array.isArray(v)) return v;
  return [];
}

/**
 * Normalize the estimate object returned from the API so every field has
 * the expected type and structure. Defensive against partial / malformed
 * AI responses.
 */
export function normalizeEstimate(est) {
  if (!est || typeof est !== "object") return null;

  const scope = est.scope_summary || {};
  return {
    ...est,
    scope_summary: {
      total_sqft: scope.total_sqft ?? null,
      bedrooms: scope.bedrooms ?? null,
      bathrooms: scope.bathrooms ?? null,
      floors: scope.floors ?? null,
      garage: safeString(scope.garage, null),
      key_features: safeArray(scope.key_features).map((f) => safeString(f)).filter(Boolean),
      plan_notes: safeArray(scope.plan_notes).map((f) => safeString(f)).filter(Boolean),
    },
    divisions: safeArray(est.divisions).map((div) => ({
      code: safeString(div?.code, "—"),
      name: safeString(div?.name, "Untitled division"),
      subtotal: safeNumber(div?.subtotal),
      line_items: safeArray(div?.line_items).map((li) => ({
        description: safeString(li?.description),
        quantity: safeNumber(li?.quantity),
        unit: safeString(li?.unit, "EA"),
        unit_cost: safeNumber(li?.unit_cost),
        material_cost: safeNumber(li?.material_cost),
        labor_cost: safeNumber(li?.labor_cost),
        equipment_cost: safeNumber(li?.equipment_cost),
        subtotal: safeNumber(li?.subtotal),
      })),
    })),
    assumptions: safeArray(est.assumptions).map((a) => safeString(a)).filter(Boolean),
    subtotal: safeNumber(est.subtotal),
    overhead_pct: safeNumber(est.overhead_pct, 10),
    overhead_amount: safeNumber(est.overhead_amount),
    profit_pct: safeNumber(est.profit_pct, 10),
    profit_amount: safeNumber(est.profit_amount),
    total: safeNumber(est.total),
    confidence: safeString(est.confidence, "medium"),
  };
}
